export type EmailCategory = 'people' | 'notif' | 'newsletter' | 'transaction';

export interface EmailAttachment {
  id: string;
  filename: string;
  size: string;
  mimeType: string;
}

export interface EmailMessage {
  id: string;
  from: string;
  fromName: string;
  to: string[];
  cc?: string[];
  date: string;
  body: string;
  attachments?: EmailAttachment[];
  quoted?: boolean;
}

export interface EmailThread {
  id: string;
  subject: string;
  snippet: string;
  messages: EmailMessage[];
  category: EmailCategory;
  isUnread: boolean;
  isStarred: boolean;
  isSnoozed: boolean;
  isArchived: boolean;
  snoozeUntil?: string;
  aiSummary?: string;
  hasAttachments?: boolean;
  time: string;
  timeFull: string;
}

export interface MockContact {
  name: string;
  email: string;
}

export const mockContacts: MockContact[] = [
  { name: 'Sarah Chen', email: 'sarah.chen@acme.co' },
  { name: 'Marcus Rodriguez', email: 'marcus.r@techventures.io' },
  { name: 'Alex Kim', email: 'alex.kim@designco.io' },
  { name: 'Lisa Wong', email: 'lisa.wong@lawfirm.com' },
  { name: 'Jordan Lee', email: 'jordan.lee@startup.io' },
  { name: 'Priya Patel', email: 'priya.patel@corp.com' },
  { name: 'Tom Bradley', email: 'tom.b@agency.co' },
  { name: 'Nina Schmidt', email: 'nina.s@euventures.de' },
];

export const mockThreads: EmailThread[] = [
  {
    id: 'thread-1',
    subject: 'Re: Vendor Contract - Final Review Needed',
    snippet: 'Attached is the final version of the vendor contract for your review...',
    category: 'people',
    isUnread: true,
    isStarred: true,
    isSnoozed: false,
    isArchived: false,
    aiSummary: 'Contract ready - review by Friday or sign and return',
    hasAttachments: true,
    time: '2h',
    timeFull: 'Mar 14, 10:32 AM',
    messages: [
      {
        id: 'msg-1-1',
        from: 'sarah.chen@acme.co',
        fromName: 'Sarah Chen',
        to: ['you@signa.app'],
        date: 'Mar 14, 10:32 AM',
        body: `Hi,

Attached is the final version of the vendor contract for your review. I've incorporated all the changes we discussed on the call.

Key terms:
- 24-month initial term
- 90-day cancellation notice  
- SOC2 compliance by Q3

Please review and sign by Friday if everything looks good. Let me know if you have any questions.

Best,
Sarah`,
        attachments: [
          { id: 'att-1', filename: 'VendorContract-v3.pdf', size: '248 KB', mimeType: 'application/pdf' },
        ],
      },
      {
        id: 'msg-1-2',
        from: 'you@signa.app',
        fromName: 'You',
        to: ['sarah.chen@acme.co'],
        date: 'Mar 14, 2:15 PM',
        body: `Sarah,

Thanks for sending this over. Reviewing now and will get back to you by Thursday EOD.

One quick question: does the SLA clause cover uptime or just response times?

-`,
      },
    ],
  },
  {
    id: 'thread-2',
    subject: 'Partnership Proposal - Follow Up',
    snippet: 'Following up on our conversation from last month about the potential partnership...',
    category: 'people',
    isUnread: true,
    isStarred: false,
    isSnoozed: false,
    isArchived: false,
    time: '3d',
    timeFull: 'Mar 10, 9:00 AM',
    messages: [
      {
        id: 'msg-2-1',
        from: 'marcus.r@techventures.io',
        fromName: 'Marcus Rodriguez',
        to: ['you@signa.app'],
        date: 'Mar 10, 9:00 AM',
        body: `Hi there,

Following up on our conversation from last month about the potential partnership between our platforms.

I've attached a brief deck outlining how we see this working. Would love to schedule a call to discuss further - are you free next Tuesday or Wednesday afternoon?

Looking forward to hearing from you.

Marcus
TechVentures IO`,
      },
    ],
  },
  {
    id: 'thread-3',
    subject: 'Your monthly report is ready',
    snippet: 'Your LinkedIn Monthly Report for February is ready...',
    category: 'newsletter',
    isUnread: false,
    isStarred: false,
    isSnoozed: false,
    isArchived: false,
    aiSummary: 'Profile views up 12% - 1,247 views this month',
    time: 'Mar 12',
    timeFull: 'Mar 12, 8:00 AM',
    messages: [
      {
        id: 'msg-3-1',
        from: 'no-reply@linkedin.com',
        fromName: 'LinkedIn',
        to: ['you@signa.app'],
        date: 'Mar 12, 8:00 AM',
        body: `Your LinkedIn Monthly Report for February is ready.

This month:
- 1,247 profile views (up 12% vs avg)
- 89 connection requests
- 4 messages from recruiters
- 3 posts impressions

View full report`,
      },
    ],
  },
  {
    id: 'thread-4',
    subject: 'Payment received - $2,400.00',
    snippet: 'Payment of $2,400.00 was successfully processed on March 12...',
    category: 'transaction',
    isUnread: false,
    isStarred: false,
    isSnoozed: false,
    isArchived: false,
    time: 'Mar 12',
    timeFull: 'Mar 12, 11:30 AM',
    messages: [
      {
        id: 'msg-4-1',
        from: 'billing@stripe.com',
        fromName: 'Stripe',
        to: ['you@signa.app'],
        date: 'Mar 12, 11:30 AM',
        body: `Payment received

Amount: $2,400.00
Payment method: Chase ****4242
Status: Succeeded

Transaction ID: ch_3OqL9sD8d

This payment was for: Pro subscription - Annual plan

Thank you for your business!`,
      },
    ],
  },
  {
    id: 'thread-5',
    subject: 'Q2 Design Sprint - Kickoff Thursday',
    snippet: "Hey! Let's sync on the Q2 design sprint plan. I've set up a FigJam board...",
    category: 'people',
    isUnread: true,
    isStarred: true,
    isSnoozed: false,
    isArchived: false,
    hasAttachments: true,
    time: 'Mar 11',
    timeFull: 'Mar 11, 4:45 PM',
    messages: [
      {
        id: 'msg-5-1',
        from: 'alex.kim@designco.io',
        fromName: 'Alex Kim',
        to: ['you@signa.app'],
        date: 'Mar 11, 4:45 PM',
        body: `Hey!

Let's sync on the Q2 design sprint plan. I've set up a FigJam board with the proposed roadmap and some initial wireframes for the new dashboard.

Can you take a look before Thursday's kickoff meeting? I want to make sure we're aligned on priorities.

Also attached is the retrospective from last quarter - some good learnings in there.

See you Thursday,
Alex`,
        attachments: [
          { id: 'att-5', filename: 'Q1-Retrospective.pdf', size: '124 KB', mimeType: 'application/pdf' },
        ],
      },
    ],
  },
  {
    id: 'thread-6',
    subject: 'Your AWS Bill - March 2026',
    snippet: 'Your estimated charges for March 2026: $847.23. This is an estimate...',
    category: 'transaction',
    isUnread: false,
    isStarred: false,
    isSnoozed: false,
    isArchived: false,
    time: 'Mar 10',
    timeFull: 'Mar 10, 6:00 AM',
    messages: [
      {
        id: 'msg-6-1',
        from: 'no-reply@aws.amazon.com',
        fromName: 'AWS',
        to: ['you@signa.app'],
        date: 'Mar 10, 6:00 AM',
        body: `Your AWS Bill - March 2026

Total estimated charges: $847.23

This is an estimate. Final charges will appear on your invoice on Mar 31.

Service breakdown:
- Amazon EC2: $412.50
- Amazon S3: $89.20
- CloudFront: $201.40
- Data Transfer: $144.13

View your AWS Billing Dashboard`,
      },
    ],
  },
  {
    id: 'thread-7',
    subject: 'Top 5 products of the day - Mar 10',
    snippet: "Here's what makers are building today. This week's spotlight: AI tools...",
    category: 'newsletter',
    isUnread: false,
    isStarred: false,
    isSnoozed: false,
    isArchived: false,
    aiSummary: 'This week spotlight: AI coding tools - demand is surging',
    time: 'Mar 10',
    timeFull: 'Mar 10, 7:30 AM',
    messages: [
      {
        id: 'msg-7-1',
        from: 'digest@producthunt.com',
        fromName: 'Product Hunt',
        to: ['you@signa.app'],
        date: 'Mar 10, 7:30 AM',
        body: `Top 5 Products of the Day - Mar 10

Here's what makers are building today.

This week's spotlight: AI coding tools are having a moment. Six of today's top 20 products are in this category.

#1 - CodePilot AI
AI code review that actually understands your codebase
+412 upvotes

#2 - Buildstream
Ship full-stack features in minutes
+287 upvotes

#3 - PersonaHQ
User research at scale with AI
+198 upvotes

See all today's products`,
      },
    ],
  },
  {
    id: 'thread-8',
    subject: 'NDAs for new hire onboarding - 3 docs',
    snippet: 'Hi, please find attached the 3 NDAs for the new engineering hires starting Monday...',
    category: 'people',
    isUnread: true,
    isStarred: false,
    isSnoozed: false,
    isArchived: false,
    hasAttachments: true,
    time: 'Mar 9',
    timeFull: 'Mar 9, 3:20 PM',
    messages: [
      {
        id: 'msg-8-1',
        from: 'lisa.wong@lawfirm.com',
        fromName: 'Lisa Wong',
        to: ['you@signa.app'],
        date: 'Mar 9, 3:20 PM',
        body: `Hi,

Please find attached the 3 NDAs for the new engineering hires starting Monday:
- Standard NDA (all employees)
- IP Assignment Agreement (engineers)
- Non-compete Addendum (executive team)

All three need to be signed before their start date. I've cc'd HR on this email as well.

Please let me know if you need anything else.

Best,
Lisa`,
        attachments: [
          { id: 'att-8a', filename: 'Standard-NDA.pdf', size: '89 KB', mimeType: 'application/pdf' },
          { id: 'att-8b', filename: 'IP-Assignment-Agreement.pdf', size: '76 KB', mimeType: 'application/pdf' },
          { id: 'att-8c', filename: 'Non-Compete-Addendum.pdf', size: '54 KB', mimeType: 'application/pdf' },
        ],
      },
    ],
  },
  {
    id: 'thread-9',
    subject: '[signa-web] Deploy succeeded',
    snippet: 'Deployment to production succeeded. 12 functions updated...',
    category: 'notif',
    isUnread: false,
    isStarred: false,
    isSnoozed: true,
    isArchived: false,
    snoozeUntil: 'Tomorrow 9:00 AM',
    time: 'Mar 9',
    timeFull: 'Mar 9, 10:15 AM',
    messages: [
      {
        id: 'msg-9-1',
        from: 'system@vercel.com',
        fromName: 'Vercel',
        to: ['you@signa.app'],
        date: 'Mar 9, 10:15 AM',
        body: `[signa-web] Deploy succeeded

Project: signa-web
Environment: Production
Build duration: 47s
Functions updated: 12

Deployment URL: https://signa.app

Changes:
- Fix auth token refresh edge case (#47)
- Update dependencies
- Improve error logging

View deployment details`,
      },
    ],
  },
  {
    id: 'thread-10',
    subject: '8 files were shared with you this week',
    snippet: 'You have new activity in shared Figma files including Signa Design System v2...',
    category: 'notif',
    isUnread: false,
    isStarred: false,
    isSnoozed: false,
    isArchived: false,
    time: 'Mar 8',
    timeFull: 'Mar 8, 9:00 AM',
    messages: [
      {
        id: 'msg-10-1',
        from: 'updates@figma.com',
        fromName: 'Figma',
        to: ['you@signa.app'],
        date: 'Mar 8, 9:00 AM',
        body: `8 files were shared with you this week

- Signa Design System v2 - 3 updates
- Mobile App Wireframes Q2 - new file
- Email Component Library - 2 updates  
- Onboarding Flow - 1 update
- Dashboard Redesign - shared by Alex Kim
- Icon Set 2026 - shared by Priya Patel
- Typography Guide - shared by Tom Bradley
- Marketing Site Components - 1 update

Open in Figma`,
      },
    ],
  },
];

export type SortOption = 'date' | 'unread' | 'starred';
export type FilterCategory = 'all' | EmailCategory;
